import React, { FC, useReducer, useEffect, useCallback, PropsWithChildren } from 'react';
import { isEmpty, find } from 'ramda';
import { action } from 'typesafe-actions';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { aliasService } from '../../../../../channels/secure-web/services';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { Account } from '../../domain/Account/entities';
import { useOnRefetchAccountDetails } from '../AccountDetails/hooks';
import { useAccountListAccounts, useOnAccountListRefetch } from '../AccountList/hooks';
import { AliasReducer } from './AliasReducer';
import { defaultAliasState } from './defaultState';
import { AliasContext, AliasDispatchContext } from './AliasContext';
import { AliasActionsEnum, AliasContextActionTypes } from './types';

export type AliasProviderProps = {
  customerId: string;
  accounts?: Account[];
  selectedAccountDetails?: Account;
  accountsRefetch?: () => void;
  accountDetailsRefetch?: () => void;
};

const AliasProvider: FC<PropsWithChildren<AliasProviderProps>> = ({
  customerId,
  accounts,
  accountsRefetch,
  accountDetailsRefetch,
  selectedAccountDetails,
  children,
}) => {
  const [state, dispatch] = useReducer(AliasReducer, defaultAliasState);

  // Conditionals
  const shouldUpsertAlias =
    state.action === AliasActionsEnum.upsert &&
    !isEmpty(state.selectedAccountForAction) &&
    Boolean(state.alias?.length) &&
    !isEmpty(aliasService);
  const shouldDeleteAlias = state.action === AliasActionsEnum.delete && !isEmpty(state.selectedAccountForAction);

  // Services
  const {
    data: upsertAliasData,
    error: upsertAliasError,
    isLoading: isLoadingUpsertAlias,
  } = useInvokeSWRService(
    aliasService.UpsertAlias,
    state.selectedAccountForAction === null
      ? {}
      : {
          customerId,
          accountId: state.selectedAccountForAction?.accountId || selectedAccountDetails?.accountId,
          // TODO:  add meta for solving caching issue
          data: {
            newAlias: state.alias,
            meta: {
              timestamp: state.selectedAccountForAction?.timestamp,
            },
          },
          successCallback: () => {
            accountsRefetch && accountsRefetch();
            accountDetailsRefetch && accountDetailsRefetch();
          },
        },
    shouldUpsertAlias,
    'aliasService.UpsertAlias',
  );

  const {
    data: deleteAliasData,
    error: deleteAliasError,
    isLoading: isLoadingDeleteAlias,
  } = useInvokeSWRService(
    aliasService.DeleteAlias,
    state.selectedAccountForAction === null
      ? {}
      : {
          customerId,
          accountId: state.selectedAccountForAction?.accountId || selectedAccountDetails?.accountId,
          successCallback: () => {
            accountsRefetch && accountsRefetch();
            accountDetailsRefetch && accountDetailsRefetch();
          },
        },
    shouldDeleteAlias,
    'aliasService.DeleteAlias',
  );

  const onAccountSelectionForAction = useCallback(
    (selectedAccount: Account) => {
      if (!selectedAccount) {
        return;
      }
      if (accounts) {
        const accountToSelect = find(account => account.accountId === selectedAccount.accountId, accounts);
        if (!accountToSelect) {
          return dispatch(action(AliasContextActionTypes.SetSelectedAccountForAction, null));
        }
        dispatch(
          action(AliasContextActionTypes.SetSelectedAccountForAction, {
            ...accountToSelect,
            timestamp: Date.now().toString(),
          }),
        );
      } else {
        dispatch(action(AliasContextActionTypes.SetSelectedAccountForAction, null));
      }
    },
    [accounts],
  );

  const onAliasAction = useCallback(
    (alias: string | undefined) => {
      if (!state.selectedAccountForAction && !selectedAccountDetails) {
        return;
      }
      if (alias?.length) {
        dispatch(
          action(AliasContextActionTypes.SetAliasAction, {
            alias,
            action: AliasActionsEnum.upsert,
          }),
        );
      } else {
        dispatch(
          action(AliasContextActionTypes.SetAliasAction, {
            alias,
            action: AliasActionsEnum.delete,
          }),
        );
      }
    },
    [state.selectedAccountForAction, selectedAccountDetails],
  );

  const onResetAccountSelectionForAction = useCallback(() => {
    dispatch(action(AliasContextActionTypes.ResetSelectedAccountForAction, null));
  }, []);

  // Reset state after edit/create alias is done
  useEffect(() => {
    const shouldResetState = !isEmpty(upsertAliasData) || !isEmpty(deleteAliasData);
    if (shouldResetState) dispatch(action(AliasContextActionTypes.ResetSelectedAccountForAction, null));
  }, [upsertAliasData, deleteAliasData]);

  return (
    <AliasContext.Provider
      value={{
        ...state,
        loadingState: {
          isLoadingUpsertAlias,
          isLoadingDeleteAlias,
        },
        errorState: {
          upsertAliasError,
          deleteAliasError,
        },
        upsertAliasData,
        deleteAliasData,
        onAliasAction,
        onAccountSelectionForAction,
        onResetAccountSelectionForAction,
      }}>
      <AliasDispatchContext.Provider value={dispatch}>{children}</AliasDispatchContext.Provider>
    </AliasContext.Provider>
  );
};

const EnhancedAliasProvider: FC<{}> = ({ children }) => {
  const accountsList = useAccountListAccounts();
  const customerId = useSelectedCustomerId();
  const onRefetchAccountList = useOnAccountListRefetch();
  const onRefetchAccountDetails = useOnRefetchAccountDetails();
  return (
    <AliasProvider
      customerId={customerId}
      accounts={accountsList?.accounts || accountsList}
      accountsRefetch={onRefetchAccountList}
      accountDetailsRefetch={onRefetchAccountDetails}>
      {children}
    </AliasProvider>
  );
};

export { AliasProvider, EnhancedAliasProvider };
